import React, { useState } from 'react';
import { Alert, Button, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import RefreshIcon from '@mui/icons-material/Refresh';
import ETVPage from '../tools/ETVPage';
import ETVTable from '../tools/ETVTable';
import ETVPopup from '../tools/ETVPopup';
import ETVMessagePopup from '../tools/ETVMessagePopup';
import ETVActionButton from '../tools/ETVActionButton';

export default function ETVPageDemo() {
  const [size, setSize] = useState('standard');
  const [loading, setLoading] = useState(false);
  const [tableSearch, setTableSearch] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const [messageType, setMessageType] = useState(null);

  const demoRows = [
    {id:1,screen:'Lobby screen',building:'One Pearl Bank',status:'Online'},
    {id:2,screen:'Lift A',building:'One Pearl Bank',status:'Offline'},
    {id:3,screen:'Reception',building:'Amber Park',status:'Online'},
    {id:4,screen:'Lift B',building:'Amber Park',status:'Maintenance'},
  ];

  const simulateLoading = () => {
    setLoading(true);
    window.setTimeout(() => setLoading(false), 1500);
  };

  return (
    <ETVPage
      width={size}
      icon={<ArchitectureIcon fontSize="large"/>}
      title="Page style demo"
      description="Reference layout for consistent Elevator TV portal pages."
      loading={loading}
      topActions={<Button startIcon={<RefreshIcon/>} onClick={simulateLoading}>Test loading</Button>}
      bottomActions={<><ETVActionButton action="cancel"/><ETVActionButton action="save"/></>}
    >
      <Stack spacing={3}>
        <Alert severity="info">This page demonstrates the shared title, icon, loading bar, content, and action areas.</Alert>
        <TextField select label="Page width" value={size} onChange={(event)=>setSize(event.target.value)} sx={{maxWidth:320}}>
          <MenuItem value="small">Small — 700px</MenuItem>
          <MenuItem value="standard">Standard — 1000px</MenuItem>
          <MenuItem value="wide">Wide — 1400px</MenuItem>
        </TextField>
        <Typography>This is the page content area. Forms, tables, calendars, or dashboards are rendered here.</Typography>
		<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
		  <ETVActionButton action="save"/>
		  <ETVActionButton action="cancel"/>
		  <ETVActionButton action="send"/>
		  <ETVActionButton action="edit"/>
		  <ETVActionButton action="update"/>
		  <ETVActionButton action="save" loading/>
		  <ETVActionButton action="send" loading/>
		</Stack>
		<Button variant="outlined" onClick={()=>setPopupOpen(true)} sx={{alignSelf:'flex-start'}}>Open popup demo</Button>
		<Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
		  <Button color="success" variant="outlined" onClick={()=>setMessageType('success')}>Success popup</Button>
		  <Button color="error" variant="outlined" onClick={()=>setMessageType('failure')}>Failure popup</Button>
		  <Button color="info" variant="outlined" onClick={()=>setMessageType('info')}>Info popup</Button>
		  <Button variant="outlined" onClick={()=>setMessageType('progress')}>Progress popup</Button>
		</Stack>
		<FormControlLabel control={<Switch checked={tableSearch} onChange={(event)=>setTableSearch(event.target.checked)}/>} label="Enable table search"/>
		<ETVTable
		  title="Standard table demo"
		  data={demoRows}
		  search={tableSearch}
		  pageSize={5}
		  columns={[
			{title:'Screen',field:'screen'},
			{title:'Building',field:'building'},
			{title:'Status',field:'status'},
		  ]}
		/>
		<ETVPopup open={popupOpen} onClose={()=>setPopupOpen(false)} width="standard"
		  icon={<ArchitectureIcon/>} title="Popup style demo" description="Reference layout for consistent dialogs."
		  loading={loading} topActions={<Button startIcon={<RefreshIcon/>} onClick={simulateLoading}>Test loading</Button>}
		  bottomActions={<><ETVActionButton action="cancel" onClick={()=>setPopupOpen(false)}/><ETVActionButton action="save"/></>}>
		  <Alert severity="info">Popup content belongs here. It uses the same title, loading, action, and spacing concepts as ETVPage.</Alert>
		</ETVPopup>
		<ETVMessagePopup open={messageType!==null} onClose={()=>setMessageType(null)} type={messageType || 'info'}
		  allowClose={true}
		  title={messageType==='success'?'Changes saved':messageType==='failure'?'Something went wrong':messageType==='progress'?'Saving changes':'Information'}
		  message={messageType==='success'?'Your changes were saved successfully.':messageType==='failure'?'The operation could not be completed. Please try again.':messageType==='progress'?'Your changes are being processed.':'This is a standardized informational message.'}
		  details={messageType==='failure'?'If the problem continues, contact support.':undefined}/>
      </Stack>
    </ETVPage>
  );
}
